// Proxies MakeHuman (yeux, sourcils, cils, dents, langue, cheveux, vêtements) : ajustement sur le
// maillage de base morphé (sémantique de shared/proxy.py), skinning et morphs propagés par le binding.
import * as THREE from 'three';
import { fetchBin, fetchJson, resolveUrl } from './fetcher';
import { viewOf, type IndexArray, type ProxyAsset } from './format';
import { computeNormals, expand3, logicalTris, setBounds } from './geometry';
import type { SparseTarget } from './targets';
import type { ClothBinding } from './collide';

export async function loadProxyAsset(baseUrl: string, id: string) {
  const assetUrl = resolveUrl(baseUrl, id + '/');
  const asset = await fetchJson<ProxyAsset>(assetUrl + 'asset.json');
  const buf = await fetchBin(assetUrl + asset.geometry);
  return { asset, buf, assetUrl };
}

export class ProxyInstance {
  readonly mesh: THREE.SkinnedMesh;
  readonly geometry = new THREE.BufferGeometry();
  readonly nVerts: number;
  readonly ref: Uint16Array;
  readonly refW: Float32Array;
  readonly offsets: Float32Array;
  readonly r2v: IndexArray;
  readonly fullIndex: IndexArray;
  readonly faceTriCount: Uint8Array;
  readonly deleteVerts: Uint16Array | null;
  /** true si le sommet n'est lié qu'à un seul sommet de base (poids 1, 0, 0). */
  readonly exact: Uint8Array;
  readonly tris: Uint32Array;                  // triangles en indices .obj
  readonly pos: Float32Array;                  // positions par sommet .obj
  private readonly nrm: Float32Array;
  private readonly rPos: Float32Array;         // positions de rendu
  private readonly rNrm: Float32Array;
  private readonly visScratch: Uint8Array;
  /** Sommets de jupe (référencés sur la "jupe d'aide") : seuls ceux-ci sont drapés en pose assise. */
  readonly skirtFree: Uint8Array | null = null;
  readonly skinIndex: Uint16Array;
  readonly skinWeight: Float32Array;
  // Drapé de la pose courante (positions/normales de rendu) et poids de fondu (voir drape.ts).
  private drapeR: Float32Array | null = null;
  private drapeN: Float32Array | null = null;
  private drapeW = 0;
  morphNames: string[] = [];

  constructor(
    readonly slot: string, readonly asset: ProxyAsset, buf: ArrayBuffer, readonly assetUrl: string,
    material: THREE.Material, skeleton: THREE.Skeleton,
    /** Plage de sommets de base de la "jupe d'aide" MakeHuman : ses vêtements reçoivent des poids lissés. */
    skirtRange?: [number, number],
  ) {
    const L = asset.layout;
    this.nVerts = asset.counts.verts;
    this.ref = viewOf<Uint16Array>(buf, L, 'refVerts');
    this.refW = viewOf<Float32Array>(buf, L, 'refWeights');
    this.offsets = viewOf<Float32Array>(buf, L, 'offsets');
    this.r2v = viewOf<IndexArray>(buf, L, 'renderToVert');
    this.fullIndex = viewOf<IndexArray>(buf, L, 'index');
    this.faceTriCount = viewOf<Uint8Array>(buf, L, 'faceTriCount');
    this.deleteVerts = L.deleteVerts ? viewOf<Uint16Array>(buf, L, 'deleteVerts') : null;
    this.exact = new Uint8Array(this.nVerts);
    for (let i = 0; i < this.nVerts; i++) this.exact[i] = this.refW[i * 3 + 1] === 0 && this.refW[i * 3 + 2] === 0 ? 1 : 0;
    this.tris = logicalTris(this.fullIndex, this.r2v);
    this.pos = new Float32Array(this.nVerts * 3);
    this.nrm = new Float32Array(this.nVerts * 3);
    const nr = asset.counts.renderVerts;
    this.rPos = new Float32Array(nr * 3);
    this.rNrm = new Float32Array(nr * 3);
    this.visScratch = new Uint8Array(this.nVerts);

    // Attributs de skinning : par sommet .obj -> par sommet de rendu
    let si = viewOf<Uint8Array>(buf, L, 'skinIndex');
    let sw = viewOf<Uint16Array>(buf, L, 'skinWeight');
    if (skirtRange) {
      [si, sw] = smoothSkirtWeights(si, sw, this.ref, this.refW, this.tris, this.nVerts, skirtRange, skeleton.bones.length);
      const [lo, hi] = skirtRange;
      const free = new Uint8Array(this.nVerts);
      let any = false;
      for (let i = 0; i < this.nVerts; i++) {
        let ok = true;
        for (let k = 0; k < 3; k++) {
          const r = this.ref[i * 3 + k];
          if (this.refW[i * 3 + k] !== 0 && (r < lo || r > hi)) ok = false;
        }
        if (ok) { free[i] = 1; any = true; }
      }
      if (any) this.skirtFree = free;
    }
    const skinIndex = new Uint16Array(nr * 4);
    const skinWeight = new Float32Array(nr * 4);
    for (let r = 0; r < nr; r++) {
      const v = this.r2v[r] * 4;
      for (let k = 0; k < 4; k++) {
        skinIndex[r * 4 + k] = si[v + k];
        skinWeight[r * 4 + k] = sw[v + k] / 65535;
      }
    }
    this.skinIndex = skinIndex;
    this.skinWeight = skinWeight;
    const g = this.geometry;
    g.setAttribute('position', new THREE.BufferAttribute(this.rPos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(this.rNrm, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(viewOf<Float32Array>(buf, L, 'uv'), 2));
    g.setAttribute('skinIndex', new THREE.BufferAttribute(skinIndex, 4));
    g.setAttribute('skinWeight', new THREE.BufferAttribute(skinWeight, 4));
    g.setIndex(new THREE.BufferAttribute(this.fullIndex.slice(), 1));

    this.mesh = new THREE.SkinnedMesh(g, material);
    this.mesh.name = `${slot}:${asset.id}`;
    this.mesh.renderOrder = asset.zDepth;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = asset.material?.castShadows ?? true;
    this.mesh.receiveShadow = asset.material?.receiveShadows ?? true;
    this.mesh.userData.zDepth = asset.zDepth;
    this.mesh.userData.assetId = asset.id;
    this.mesh.bind(skeleton, new THREE.Matrix4());
  }

  /**
   * Ajuste le proxy sur les positions de base (shared/proxy.py getCoords) :
   *   p = Σ w_k · base[ref_k] + M · offset,  M = diag(|base[a].x - base[b].x| / ref_x, …).
   */
  fit(base: Float32Array, collide?: (b: ClothBinding) => void) {
    const { ref, refW, offsets, pos } = this;
    let sx = 1, sy = 1, sz = 1;
    const sc = this.asset.scale;
    if (sc) {
      const s = (k: number) => {
        const [a, b, den] = sc[k];
        return Math.abs(base[a * 3 + k] - base[b * 3 + k]) / den;
      };
      sx = s(0); sy = s(1); sz = s(2);
    }
    for (let i = 0; i < this.nVerts; i++) {
      const o = i * 3;
      const a = ref[o] * 3, b = ref[o + 1] * 3, c = ref[o + 2] * 3;
      const wa = refW[o], wb = refW[o + 1], wc = refW[o + 2];
      pos[o] = wa * base[a] + wb * base[b] + wc * base[c] + sx * offsets[o];
      pos[o + 1] = wa * base[a + 1] + wb * base[b + 1] + wc * base[c + 1] + sy * offsets[o + 1];
      pos[o + 2] = wa * base[a + 2] + wb * base[b + 2] + wc * base[c + 2] + sz * offsets[o + 2];
    }
    // Vêtements : garantit un écart minimal au-dessus de la peau (voir collide.ts).
    collide?.(this);
    // Nouvelle forme : l'ancien drapé ne correspond plus (avant le vent, qui en dépend).
    this.drapeR = this.drapeN = null;
    this.drapeW = 0;
    // Vent : part de l'ourlet (0 en haut de la jupe → 1 en bas), d'après la forme ajustée.
    if (this.skirtFree) this.updateFlutter();
    computeNormals(pos, this.tris, this.nrm);
    expand3(pos, this.r2v, this.rPos);
    expand3(this.nrm, this.r2v, this.rNrm);
    const g = this.geometry;
    g.attributes.position.needsUpdate = true;
    g.attributes.normal.needsUpdate = true;
    setBounds(g, this.rPos);
    this.mesh.boundingSphere = g.boundingSphere!.clone();
    this.mesh.boundingBox = g.boundingBox!.clone();
  }

  private updateFlutter() {
    const free = this.skirtFree!, pos = this.pos;
    let top = -Infinity, bottom = Infinity;
    for (let i = 0; i < this.nVerts; i++) if (free[i]) { top = Math.max(top, pos[i * 3 + 1]); bottom = Math.min(bottom, pos[i * 3 + 1]); }
    const span = Math.max(0.05, top - bottom);
    const nr = this.r2v.length;
    let attr = this.geometry.getAttribute('flutter') as THREE.BufferAttribute | undefined;
    if (!attr) { attr = new THREE.BufferAttribute(new Float32Array(nr), 1); this.geometry.setAttribute('flutter', attr); }
    const a = attr.array as Float32Array;
    this.flutterBase = new Float32Array(nr);
    for (let r = 0; r < nr; r++) {
      const v = this.r2v[r];
      this.flutterBase[r] = free[v] ? Math.min(1, Math.max(0, (top - pos[v * 3 + 1]) / span)) : 0;
      a[r] = this.flutterBase[r] * (1 - this.drapeW);
    }
    attr.needsUpdate = true;
  }

  /**
   * Ancrage sur la peau (collision tissu / peau au rendu, voir HumanImpl.updateAnchors), par sommet de rendu :
   * position de repos du point de peau (w = écart minimal à garder, 0 = pas d'ancrage), sa normale, ses os et poids.
   */
  setAnchors(pos: Float32Array, nrm: Float32Array, idx: Float32Array, wgt: Float32Array) {
    const g = this.geometry;
    const set = (name: string, a: Float32Array, k: number) => {
      const old = g.getAttribute(name) as THREE.BufferAttribute | undefined;
      if (old && old.array.length === a.length) { (old.array as Float32Array).set(a); old.needsUpdate = true; }
      else g.setAttribute(name, new THREE.BufferAttribute(a, k));
    };
    set('anchorPos', pos, 4);
    set('anchorNrm', nrm, 3);
    set('anchorIdx', idx, 4);
    set('anchorWgt', wgt, 4);
  }

  /** Flottement au vent avant drapé (une jupe posée sur les cuisses ou au sol ne flotte plus). */
  private flutterBase: Float32Array | null = null;

  /** Drapé calculé pour la pose courante (positions de liaison par sommet .obj) ou null pour l'oublier. */
  setDrape(objPos: Float32Array | null) {
    if (!objPos) { this.drapeR = this.drapeN = null; return; }
    const n = new Float32Array(this.nVerts * 3);
    computeNormals(objPos, this.tris, n);
    this.drapeR ??= new Float32Array(this.rPos.length);
    this.drapeN ??= new Float32Array(this.rNrm.length);
    expand3(objPos, this.r2v, this.drapeR);
    expand3(n, this.r2v, this.drapeN);
  }

  get hasDrape() { return this.drapeR !== null; }

  /** Fondu entre la forme ajustée (0) et le drapé (1). */
  blendDrape(w: number) {
    if (!this.drapeR) w = 0;
    if (w === this.drapeW) return;
    this.drapeW = w;
    const fl = this.geometry.getAttribute('flutter') as THREE.BufferAttribute | undefined;
    if (fl && this.flutterBase) {
      const a = fl.array as Float32Array, b = this.flutterBase;
      for (let r = 0; r < a.length; r++) a[r] = b[r] * (1 - w);
      fl.needsUpdate = true;
    }
    const P = this.rPos, N = this.rNrm;
    expand3(this.pos, this.r2v, P);
    expand3(this.nrm, this.r2v, N);
    if (w > 0) {
      const D = this.drapeR!, DN = this.drapeN!;
      for (let i = 0; i < P.length; i++) { P[i] += (D[i] - P[i]) * w; N[i] += (DN[i] - N[i]) * w; }
    }
    const g = this.geometry;
    g.attributes.position.needsUpdate = true;
    g.attributes.normal.needsUpdate = true;
  }

  /**
   * Masque les faces dont tous les sommets sont cachés (transferVertexMaskToProxy) :
   * baseVisible = masque de sommets de base (1 = visible) accumulé par les couches supérieures.
   */
  applyMask(baseVisible: Uint8Array | null) {
    const g = this.geometry;
    if (!baseVisible) {
      (g.index!.array as IndexArray).set(this.fullIndex);
      g.setDrawRange(0, this.fullIndex.length);
      g.index!.needsUpdate = true;
      return;
    }
    const vis = this.visScratch;
    const { ref } = this;
    for (let i = 0; i < this.nVerts; i++) {
      const o = i * 3;
      vis[i] = this.exact[i] ? baseVisible[ref[o]] : (baseVisible[ref[o]] + baseVisible[ref[o + 1]] + baseVisible[ref[o + 2]] >= 2 ? 1 : 0);
    }
    const out = g.index!.array as IndexArray;
    const src = this.fullIndex, r2v = this.r2v;
    let w = 0, t = 0;
    for (let f = 0; f < this.faceTriCount.length; f++) {
      const n = this.faceTriCount[f] * 3;
      let visible = false;
      for (let k = t; k < t + n && !visible; k++) visible = vis[r2v[src[k]]] === 1;
      if (visible) for (let k = t; k < t + n; k++) out[w++] = src[k];
      t += n;
    }
    g.setDrawRange(0, w);
    g.index!.needsUpdate = true;
  }

  /**
   * Morphs temps réel propagés depuis les cibles du corps : delta(proxy) = Σ w_k · delta(base[ref_k]).
   * Seuls les morphs qui déplacent effectivement le proxy sont ajoutés.
   */
  buildMorphs(morphs: { name: string; target: SparseTarget }[], scratch: Float32Array, mark: Uint8Array) {
    const g = this.geometry;
    const attrs: THREE.BufferAttribute[] = [];
    const names: string[] = [];
    const objDelta = new Float32Array(this.nVerts * 3);
    for (const { name, target } of morphs) {
      const { idx, d, scale } = target;
      for (let i = 0; i < idx.length; i++) {
        const o = idx[i] * 3;
        scratch[o] = d[i * 3] * scale; scratch[o + 1] = d[i * 3 + 1] * scale; scratch[o + 2] = d[i * 3 + 2] * scale;
        mark[idx[i]] = 1;
      }
      let any = false;
      for (let i = 0; i < this.nVerts; i++) {
        const o = i * 3;
        const a = this.ref[o], b = this.ref[o + 1], c = this.ref[o + 2];
        const wa = this.refW[o], wb = this.refW[o + 1], wc = this.refW[o + 2];
        if (!((mark[a] && wa) || (mark[b] && wb) || (mark[c] && wc))) {
          objDelta[o] = objDelta[o + 1] = objDelta[o + 2] = 0;
          continue;
        }
        any = true;
        for (let k = 0; k < 3; k++) objDelta[o + k] = wa * scratch[a * 3 + k] + wb * scratch[b * 3 + k] + wc * scratch[c * 3 + k];
      }
      for (let i = 0; i < idx.length; i++) {
        const o = idx[i] * 3;
        scratch[o] = scratch[o + 1] = scratch[o + 2] = 0;
        mark[idx[i]] = 0;
      }
      if (!any) continue;
      const r = new Float32Array(this.r2v.length * 3);
      expand3(objDelta, this.r2v, r);
      const attr = new THREE.BufferAttribute(r, 3);
      attr.name = name;
      attrs.push(attr);
      names.push(name);
    }
    this.morphNames = names;
    if (attrs.length) {
      g.morphAttributes.position = attrs;
      g.morphTargetsRelative = true;
      this.mesh.updateMorphTargets();
    }
  }

  dispose() {
    this.geometry.dispose();
    this.mesh.removeFromParent();
  }
}

/**
 * Jupes et robes : les poids hérités de la "jupe d'aide" MakeHuman sautent d'une cuisse à l'autre, ce qui
 * déchire le tissu dès qu'elle s'assoit ou croise les jambes. On lisse les poids sur la topologie du vêtement
 * (sommets de jupe uniquement ; le haut du vêtement sert de bord fixe), puis on garde les 4 plus forts.
 */
function smoothSkirtWeights(
  si: Uint8Array, sw: Uint16Array, ref: Uint16Array, refW: Float32Array, tris: Uint32Array,
  nVerts: number, [lo, hi]: [number, number], nBones: number, iterations = 10,
): [Uint8Array, Uint16Array] {
  const skirt = new Uint8Array(nVerts);
  let count = 0;
  for (let v = 0; v < nVerts; v++) {
    for (let k = 0; k < 3; k++) {
      const r = ref[v * 3 + k];
      if (refW[v * 3 + k] > 0 && r >= lo && r <= hi) { skirt[v] = 1; count++; break; }
    }
  }
  if (!count) return [si, sw];
  // Poids denses (sommet × os).
  const W = new Float32Array(nVerts * nBones);
  for (let v = 0; v < nVerts; v++) for (let k = 0; k < 4; k++) W[v * nBones + si[v * 4 + k]] += sw[v * 4 + k] / 65535;
  // Voisinage par arêtes de triangles.
  const nb: number[][] = Array.from({ length: nVerts }, () => []);
  for (let t = 0; t < tris.length; t += 3) {
    const a = tris[t], b = tris[t + 1], c = tris[t + 2];
    nb[a].push(b, c); nb[b].push(a, c); nb[c].push(a, b);
  }
  const N = new Float32Array(W.length);
  for (let it = 0; it < iterations; it++) {
    N.set(W);
    for (let v = 0; v < nVerts; v++) {
      if (!skirt[v] || !nb[v].length) continue;
      const o = v * nBones, inv = 0.5 / nb[v].length;
      for (let b = 0; b < nBones; b++) {
        let acc = 0;
        for (const u of nb[v]) acc += W[u * nBones + b];
        N[o + b] = 0.5 * W[o + b] + acc * inv;
      }
    }
    W.set(N);
  }
  const outI = new Uint8Array(si), outW = new Uint16Array(sw);
  for (let v = 0; v < nVerts; v++) {
    if (!skirt[v]) continue;
    const o = v * nBones;
    const top: Array<[number, number]> = [];
    for (let b = 0; b < nBones; b++) if (W[o + b] > 1e-4) top.push([b, W[o + b]]);
    top.sort((x, y) => y[1] - x[1]);
    const keep = top.slice(0, 4);
    const sum = keep.reduce((s, [, w]) => s + w, 0) || 1;
    for (let k = 0; k < 4; k++) {
      outI[v * 4 + k] = keep[k]?.[0] ?? 0;
      outW[v * 4 + k] = Math.round(((keep[k]?.[1] ?? 0) / sum) * 65535);
    }
  }
  return [outI, outW];
}
